import {
  ChangeDetectionStrategy,
  Component,
  signal,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  input,
} from '@angular/core';
import { AuthService } from '../../../../services/auth';
import { PeoplePickerList } from '../../../model/recipes-interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-people-picker',
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    TranslateModule,
    NgClass,
  ],
  templateUrl: './people-picker.component.html',
  styleUrl: './people-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeoplePickerComponent {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly peoplePickerList = signal<PeoplePickerList[]>([]);
  public userControl = new FormControl('');
  public selectedUserMail: PeoplePickerList[] = [];
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement> | undefined;
  @Output() selectedUserMailChange = new EventEmitter<PeoplePickerList[]>();
  public isQueryBuilder = input<boolean>(false);

  constructor(private _authService: AuthService) {
    this.updateValueChange();
  }

  /**
   * Updates the value change listener for the user control to fetch valid users.
   *
   * @private
   * @memberof PeoplePickerComponent
   */
  private updateValueChange() {
    this.userControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (value && value.length >= 2) {
          this.userControl.setValue(' ', { emitEvent: false });
          this.getValidUserList(value);
        } else {
          this.peoplePickerList.set([]);
        }
      });
  }

  /**
   * Gets the list of valid users based on the input.
   *
   * @private
   * @param {(string | null)} input
   * @memberof PeoplePickerComponent
   */
  public async getValidUserList(userInput?: string | null) {
    const peoplePickerList = await this._authService.getPeoplePickerData(
      userInput || '',
    );
    this.peoplePickerList.set(peoplePickerList.value || []);
  }

  /**
   * Removes a user from the people picker list.
   *
   * @param {*} user
   * @memberof PeoplePickerComponent
   */
  public removeUser(user: PeoplePickerList): void {
    const index = this.selectedUserMail.findIndex((u) => u.mail === user.mail);
    if (index >= 0) {
      this.selectedUserMail.splice(index, 1);
    }
    this.selectedUserMailChange.emit(this.selectedUserMail);
  }

  /**
   * Handles the selection of a user from the autocomplete options.
   *
   * @param {*} event
   * @memberof PeoplePickerComponent
   */
  public selectedUser(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value;
    this.selectedUserMail.push(user as PeoplePickerList);
    if (this.userInput) {
      this.userInput.nativeElement.value = '';
      this.userInput.nativeElement.focus();
      this.userInput.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    this.selectedUserMailChange.emit(this.selectedUserMail);
  }
}
